import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import Decimal from 'decimal.js';
import { uniqBy, xor } from 'lodash-es';
import { convertStrToRichText } from 'seagull';
import { isBoolField, isBoolTrue, createErrorObject, parseCsv, validateCsvColumns, addCsvLineNumbers, validateCsvRow, normalizeSpaces } from '~/components/ImportWizard/ImportWizard.utils';
import { decimalFromStr } from '~/infra/utils';
import { serviceTemplateModel } from '~/models';
import { ServiceCostType } from '~/models/service-cost/model';
import { BillingTriggerType } from '~/types/api.model';
import { extractBundles, validateBundleColumnsConsistency, validateBundleServicesSize, validateBundleUniqueIncludedServices } from './bundlesValidation.utils';
import { discountTypeOptions, errorMessages, getBillingOccurrenceValidations, getPricingValidations, getServiceNameValidations, bundleColumnsToValidate, getBundlePricingType, getServiceCost, isRowOfBundle, bundleRelatedColumns, getUnitCapValidations, forbiddenServiceColumnsInBundleRows } from './servicesValidation.utils';
export const getServicesCsvColumnConfig = (options)=>{
    const columnConfig = _object_spread({
        ['service name']: _object_spread_props(_object_spread({
            inputName: 'serviceName',
            required: true,
            manipulateValue: normalizeSpaces
        }, getServiceNameValidations()), {
            requiredError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, errorMessages.getEmptyStringError(headerName))
        }),
        ['service description']: {
            inputName: 'serviceDescription'
        },
        ['automatic billing']: {
            inputName: 'automaticBilling',
            required: true,
            requiredError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, errorMessages.getInvalidBooleanError(headerName)),
            validate: isBoolField,
            validateError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, errorMessages.getInvalidBooleanError(headerName))
        },
        ['billing occurrence']: _object_spread({
            inputName: 'billingOccurrence'
        }, getBillingOccurrenceValidations({
            excludeValues: [
                'in stages'
            ],
            actLikeManualIfBundledService: options.UPLOAD_BUNDLES_BULK
        })),
        ['pricing']: _object_spread({
            inputName: 'pricing'
        }, getPricingValidations({
            actLikeManualIfBundledService: options.UPLOAD_BUNDLES_BULK,
            excludeValues: []
        })),
        ['rate']: {
            inputName: 'rate',
            required: true,
            requiredError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, errorMessages.priceError),
            validate: (price, param)=>{
                let { rowObject } = param;
                const isRangePricing = rowObject.pricing.toLowerCase() === 'range';
                if (price.startsWith('-')) {
                    return {
                        errorType: isRangePricing ? 'rangePriceError' : 'priceError'
                    };
                }
                // split in case of range price format ${min}-${max}
                const priceSplit = price.split('-');
                const trimmedPrices = priceSplit.map((p)=>p.trim());
                const hasMoreThanOnePrice = priceSplit.length > 1 && priceSplit[0].length && priceSplit[1].length;
                if (hasMoreThanOnePrice) {
                    if (!isRangePricing) return {
                        errorType: 'incompatiblePriceAndPricing'
                    };
                    const [first, second] = trimmedPrices;
                    const min = decimalFromStr(first);
                    const max = decimalFromStr(second);
                    if (min && max && min.gt(max)) return {
                        errorType: 'minPriceExceedMaxPrice'
                    };
                }
                const validPrices = trimmedPrices.every((p)=>{
                    const decimal = decimalFromStr(p);
                    return !!decimal && decimal.isPositive();
                });
                return validPrices || {
                    errorType: isRangePricing ? 'rangePriceError' : 'priceError'
                };
            },
            validateError: (headerName, rowNumber, errorType)=>{
                switch(errorType){
                    case 'priceError':
                        return createErrorObject(headerName, rowNumber, errorMessages.priceError);
                    case 'rangePriceError':
                        return createErrorObject(headerName, rowNumber, errorMessages.rangePriceError);
                    case 'incompatiblePriceAndPricing':
                        return createErrorObject(headerName, rowNumber, errorMessages.incompatiblePriceAndPricingMsg);
                    case 'minPriceExceedMaxPrice':
                        return createErrorObject(headerName, rowNumber, errorMessages.minPriceExceedingError);
                }
            }
        },
        ['unit cap']: _object_spread({
            inputName: 'unitCap'
        }, getUnitCapValidations()),
        ['unit name']: {
            inputName: 'unitName',
            requiredWhen: (param)=>{
                let { pricing } = param;
                return pricing.toLowerCase() === 'quantity';
            },
            requiredError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, errorMessages.getEmptyStringError(headerName))
        },
        ['discount']: {
            inputName: 'discount',
            validate: (discount, param)=>{
                let { rowObject } = param;
                var _rowObject_discountType;
                if ((discount === null || discount === void 0 ? void 0 : discount.length) === 0) return true;
                const decimal = decimalFromStr(discount);
                if (!decimal) return {
                    errorType: 'negative'
                };
                const isPositiveNumber = decimal.isPositive();
                if (!isPositiveNumber) {
                    return {
                        errorType: 'negative'
                    };
                }
                const discountType = ((_rowObject_discountType = rowObject.discountType) === null || _rowObject_discountType === void 0 ? void 0 : _rowObject_discountType.toLowerCase()) || '';
                if (discountType === 'percent' && decimal.gt(100)) {
                    return {
                        errorType: 'percentLargerThan100'
                    };
                }
                if (discountType === 'flat' && rowObject.pricing.toLowerCase() !== 'range' && decimal.gt(rowObject.rate)) {
                    return {
                        errorType: 'flatLargerThanPrice'
                    };
                }
                return true;
            },
            validateError: (headerName, rowNumber, errorType)=>{
                switch(errorType){
                    case 'negative':
                        return createErrorObject(headerName, rowNumber, errorMessages.positiveError);
                    case 'percentLargerThan100':
                        return createErrorObject(headerName, rowNumber, errorMessages.percentDiscountLargerThan100);
                    case 'flatLargerThanPrice':
                        return createErrorObject(headerName, rowNumber, errorMessages.flatDiscountGreaterThanPrice);
                }
            }
        },
        ['discount type']: {
            inputName: 'discountType',
            validate: (discountType, param)=>{
                let { rowObject } = param;
                const excludedDiscountTypes = [
                    'flat'
                ];
                const isRelateToBundle = options.UPLOAD_BUNDLES_BULK && isRowOfBundle(rowObject);
                const normalizedDiscountType = discountType === null || discountType === void 0 ? void 0 : discountType.toLowerCase();
                const valid = !normalizedDiscountType || discountTypeOptions.includes(normalizedDiscountType) && (!isRelateToBundle || !excludedDiscountTypes.includes(normalizedDiscountType));
                return {
                    valid,
                    errorType: valid ? undefined : isRelateToBundle ? 'flatNotAllowed' : 'invalidValue'
                };
            },
            validateError: (headerName, rowNumber, errorType)=>{
                const excludeValues = errorType === 'flatNotAllowed' ? [
                    'flat'
                ] : [];
                return createErrorObject(headerName, rowNumber, errorMessages.getDiscountTypeError({
                    excludeValues
                }));
            }
        }
    }, options.UPLOAD_BUNDLES_BULK ? {
        ['bundle name']: _object_spread({
            inputName: 'bundleName',
            manipulateValue: normalizeSpaces,
            requiredWhen: (rowObject)=>bundleRelatedColumns.some((column)=>rowObject[column]),
            requiredError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, errorMessages.bundleNameIsRequired)
        }, getServiceNameValidations({
            isBundleName: true
        })),
        ['bundle description']: {
            inputName: 'bundleDescription',
            required: false
        },
        ['bundle billing occurrence']: _object_spread({
            inputName: 'bundleBillingOccurrence',
            requiredWhen: (param)=>{
                let { bundleName } = param;
                return !!bundleName;
            },
            requiredError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, errorMessages.bundleBillingOccurrenceIsRequired)
        }, getBillingOccurrenceValidations({
            excludeValues: [
                'in stages'
            ],
            isOptional: true
        })),
        ['bundle pricing']: _object_spread({
            inputName: 'bundlePricing',
            requiredWhen: (param)=>{
                let { bundleName } = param;
                return !!bundleName;
            },
            requiredError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, errorMessages.bundlePricingIsRequired)
        }, getPricingValidations({
            excludeValues: [
                'range'
            ],
            isOptional: true
        })),
        ['bundle unit name']: {
            inputName: 'bundleUnitName',
            requiredWhen: (param)=>{
                let { bundleName, bundlePricing } = param;
                return !!bundleName && (bundlePricing === null || bundlePricing === void 0 ? void 0 : bundlePricing.toLowerCase()) === 'quantity';
            },
            requiredError: (headerName, rowNumber)=>createErrorObject(headerName, rowNumber, errorMessages.getEmptyStringError(headerName))
        },
        ['bundle unit cap']: _object_spread({
            inputName: 'bundleUnitCap'
        }, getUnitCapValidations())
    } : {});
    if (options.forbidServiceColumnsInBundleRows) {
        Object.keys(columnConfig).forEach((column)=>{
            if (forbiddenServiceColumnsInBundleRows.includes(column)) {
                columnConfig[column].getForbiddenReason = (rowObject)=>{
                    if (isRowOfBundle(rowObject)) {
                        return errorMessages.serviceFieldShouldNotBeSet;
                    }
                    return null;
                };
            }
        });
    }
    return columnConfig;
};
function validateServicesOfBundles(bundleRows) {
    let existingServiceTemplates = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : [], nonBundleRows = arguments.length > 2 ? arguments[2] : void 0;
    const isServiceExists = (serviceName)=>existingServiceTemplates.some((s)=>s.name === serviceName) || nonBundleRows.some((s)=>s.serviceName === serviceName);
    return bundleRows.reduce((acc, row)=>{
        const serviceName = row.serviceName;
        if (!isServiceExists(serviceName)) {
            acc.push(createErrorObject('service name', row.lineNumber, errorMessages.getBundleServiceNotFoundError(serviceName)));
        }
        return acc;
    }, []);
}
export function runBundleValidation(data, columnsToValidate) {
    let existingServiceTemplates = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : [];
    if (!data.length) return [];
    const errors = [];
    const bundleServices = {};
    const bundlesColumnDataToValidate = data.map((row)=>{
        if (existingServiceTemplates.some((s)=>s.name === row.bundleName)) {
            errors.push(createErrorObject('bundle name', row.lineNumber, errorMessages.nonUniqueBundleNameError));
        }
        if (!bundleServices[row.bundleName]) {
            bundleServices[row.bundleName] = {
                services: [],
                lineNumber: row.lineNumber
            };
        }
        bundleServices[row.bundleName].services.push(row);
        const validationColumns = columnsToValidate.reduce((acc, column)=>{
            const value = row[column];
            acc[column] = (value != null ? String(value) : '').toLowerCase();
            return acc;
        }, {});
        return _object_spread({
            bundleName: row.bundleName,
            lineNumber: row.lineNumber
        }, validationColumns);
    });
    const bundleUniqueServicesErrors = validateBundleUniqueIncludedServices(bundleServices);
    const bundleServicesSizeErrors = validateBundleServicesSize(bundleServices);
    const bundleColumnsConsistencyErrors = validateBundleColumnsConsistency(bundlesColumnDataToValidate, columnsToValidate);
    return [
        ...errors,
        ...bundleUniqueServicesErrors,
        ...bundleServicesSizeErrors,
        ...bundleColumnsConsistencyErrors
    ];
}
function runServicesValidation(data, existingServiceTemplates) {
    if (!data.length) return [];
    const errors = [];
    const nonUniqueServicesByName = xor(data, uniqBy(existingServiceTemplates.map((s)=>({
            lineNumber: -1,
            serviceName: s.name
        })).concat(data), 'serviceName')).filter((s)=>s.lineNumber !== -1);
    if (nonUniqueServicesByName.length) {
        nonUniqueServicesByName.forEach((rowObject)=>errors.push(createErrorObject('service name', rowObject.lineNumber, errorMessages.nonUniqueServiceNameError)));
    }
    return errors;
}
const getServiceBilling = function getServiceBilling(row) {
    let billingField = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : 'billingOccurrence';
    const isAutomaticBilling = isBoolTrue(row.automaticBilling);
    const normalizedRecurrencePeriod = row[billingField].toLowerCase();
    const isRecurring = normalizedRecurrencePeriod !== 'one time';
    const billingTrigger = isAutomaticBilling ? isRecurring ? BillingTriggerType.Ongoing : BillingTriggerType.OnApproval : isRecurring ? BillingTriggerType.RepeatableManual : BillingTriggerType.OneTimeManual;
    switch(billingTrigger){
        case BillingTriggerType.OnApproval:
            return serviceTemplateModel.makeBillingOnApproval();
        case BillingTriggerType.OneTimeManual:
            return serviceTemplateModel.makeBillingOneTimeManual();
        case BillingTriggerType.Ongoing:
            return serviceTemplateModel.makeBillingOngoing(normalizedRecurrencePeriod);
        case BillingTriggerType.RepeatableManual:
            return serviceTemplateModel.makeBillingRepeatableManual(normalizedRecurrencePeriod);
    }
};
const createServiceTemplate = function createServiceTemplate(row) {
    let { id, ignoreServiceDiscount } = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {
        id: null,
        ignoreServiceDiscount: false
    };
    return serviceTemplateModel.makeServiceTemplate({
        id,
        name: row.serviceName,
        description: convertStrToRichText(row.serviceDescription),
        cost: getServiceCost(row, {
            ignoreServiceDiscount
        }),
        billing: getServiceBilling(row)
    });
};
function createBundles(parsedCsv, existingServiceTemplates) {
    const bundles = parsedCsv.reduce((acc, row)=>{
        var _ref;
        const bundleName = row.bundleName;
        if (!acc[bundleName]) {
            acc[bundleName] = {
                name: bundleName,
                description: row.bundleDescription,
                billing: getServiceBilling(row, 'bundleBillingOccurrence'),
                bundle: {
                    pricingType: getBundlePricingType(row),
                    services: []
                },
                accumulatedCost: new Decimal(0),
                row
            };
        }
        const bundle = acc[bundleName];
        const existingService = existingServiceTemplates.find((s)=>s.name === row.serviceName);
        const id = (_ref = existingService === null || existingService === void 0 ? void 0 : existingService.id) !== null && _ref !== void 0 ? _ref : null;
        const currentService = createServiceTemplate(row, {
            id,
            ignoreServiceDiscount: true
        });
        const serviceCost = currentService.cost.pricingType === ServiceCostType.Range ? currentService.cost.maxPrice : currentService.cost.price;
        bundle.bundle.services.push(_object_spread_props(_object_spread({}, currentService), {
            rate: serviceCost
        }));
        bundle.accumulatedCost = bundle.accumulatedCost.add(serviceCost);
        return acc;
    }, {});
    return Object.values(bundles).map((_0)=>{
        let { accumulatedCost, row } = _0, bundle = _object_without_properties(_0, [
            "accumulatedCost",
            "row"
        ]);
        return serviceTemplateModel.makeServiceTemplate(_object_spread_props(_object_spread({}, bundle), {
            cost: getServiceCost(row, {
                useBundleFields: true,
                customRate: accumulatedCost.toString()
            })
        }));
    });
}
const createServices = (parsedCsv)=>{
    return parsedCsv.map((row)=>createServiceTemplate(row));
};
function tryToParseError(error, or) {
    try {
        return JSON.parse(error);
    } catch (e) {
        return or;
    }
}
export const parseAndValidateServicesCsv = async function parseAndValidateServicesCsv(data, existingServiceTemplates) {
    let options = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : {};
    try {
        const columnConfig = getServicesCsvColumnConfig(_object_spread_props(_object_spread({}, options), {
            forbidServiceColumnsInBundleRows: true
        }));
        const parsedData = await parseCsv(data, columnConfig);
        if (!parsedData.length) throw new Error('No data');
        validateCsvColumns(parsedData, columnConfig);
        const withLineNumbers = addCsvLineNumbers(parsedData);
        const { bundleRows, nonBundleRows } = extractBundles(withLineNumbers);
        const errors = [
            ...withLineNumbers.reduce((acc, rowObject)=>{
                return [
                    ...acc,
                    ...validateCsvRow(rowObject, columnConfig)
                ];
            }, []),
            ...runServicesValidation(nonBundleRows, existingServiceTemplates),
            ...options.UPLOAD_BUNDLES_BULK ? [
                ...runBundleValidation(bundleRows, bundleColumnsToValidate, existingServiceTemplates),
                ...validateServicesOfBundles(bundleRows, existingServiceTemplates, nonBundleRows)
            ] : []
        ];
        if (!errors.length) {
            const newServices = createServices(nonBundleRows);
            const items = [
                ...newServices,
                ...options.UPLOAD_BUNDLES_BULK ? createBundles(bundleRows, [
                    ...existingServiceTemplates,
                    ...newServices
                ]) : []
            ];
            return {
                items,
                errors
            };
        } else {
            return {
                items: [],
                errors
            };
        }
    } catch (exn) {
        var _tryToParseError;
        return {
            items: [],
            errors: [
                {
                    message: '' + exn,
                    rowNumber: (exn === null || exn === void 0 ? void 0 : exn.message) != null ? (_tryToParseError = tryToParseError(exn.message, 0)) === null || _tryToParseError === void 0 ? void 0 : _tryToParseError.lineNumber : 0
                }
            ]
        };
    }
};
